import { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  flexBoxCenter,
  justifyContentSpaceBetween,
  layerA,
  layerA180Deg,
  layerADark,
  layerADisabled180Deg,
  layerBDisabled,
} from 'shared/styles/commonStyles';

import InputTempMessage from 'shared/components/userMessages/inputTempMessage';
import SelectLocations from 'features/masterControl/components/switches/SelectLocations';

import {
  MobileWrapper,
  ScopeWrapper,
  MobileHole,
  MobileTop,
  SectionCircle,
  CircleButton,
  CircleHole,
  CircleTop,
  LogoImg,
  SectionSelect,
  MobileMessageBoxWrapper,
} from './styles/mobileLayout';


const MobileControlLayout = ({
  scope,
  swtName,
  specificLocation,
  controlName,
  logoSrc,
  label,
  labelSmall,
  disabled,
  hasInput,
  tempInput,
  onTempChange,
  placeholder,
  onApply,
  onSubmit,
  subtitle,
  messages,
  openMessageBox,
  onCloseMessageBox,
  extraExpandedContent,
  extraLocationContent,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpandButton = (e) => {
    e.preventDefault();
    setIsExpanded((prev) => !prev);
  };

  const expandIcon = disabled
    ? '/images/MC-expand-disabled.svg'
    : isExpanded
    ? '/images/MC-expand-true.svg'
    : '/images/MC-expand-false.svg';

  const wide = !hasInput;

  const messageBox = openMessageBox && (
    <MobileMessageBoxWrapper>
      <InputTempMessage
        onClose={onCloseMessageBox}
        title='master control'
        subtitle={subtitle}
        messages={messages}
        isMobile
      />
    </MobileMessageBoxWrapper>
  );

  if (scope === 'switch') {
    return (
      <MobileWrapper isExpanded={isExpanded} disabled={disabled}>
        <MobileFormController $isMargin={isExpanded}>
          <MobileHole>
            <MobileTop disabled={disabled}>
              <SectionCircle disabled={disabled} onClick={onApply}>
                <CircleButton disabled={disabled}>
                  <CircleHole disabled={disabled}>
                    <LogoImg src={logoSrc} />
                  </CircleHole>
                </CircleButton>
              </SectionCircle>

              {hasInput && (
                <MobileInputTemp
                  type='text'
                  onChange={onTempChange}
                  value={tempInput}
                  placeholder={placeholder}
                  disabled={disabled}
                />
              )}

              <MobileHeatSection $wide={wide} disabled={disabled}>
                <MobileHeatBtn onClick={onApply} $wide={wide} disabled={disabled}>
                  {label}
                </MobileHeatBtn>
              </MobileHeatSection>

              <SectionCircle disabled={disabled}>
                <CircleButton
                  disabled={disabled}
                  isExpanded={isExpanded}
                  onClick={handleExpandButton}
                >
                  <CircleHole disabled={disabled}>
                    <CircleTop isExpanded={isExpanded} disabled={disabled}>
                      <LogoImg isSm src={expandIcon} />
                    </CircleTop>
                  </CircleHole>
                </CircleButton>
              </SectionCircle>
            </MobileTop>
          </MobileHole>
        </MobileFormController>

        {isExpanded && (
          <>
            <SelectLocations
              name={controlName}
              scope={scope}
              swtName={swtName}
              specificLocation={specificLocation}
              disabled={disabled}
            />
            {extraExpandedContent}
          </>
        )}

        {messageBox}
      </MobileWrapper>
    );
  }


  return (
    <MobileWrapper isSmall disabled={disabled}>
      <SectionSelect>
        <SelectLocations
          name={controlName}
          scope={scope}
          swtName={swtName}
          specificLocation={specificLocation}
          disabled={disabled}
        />
      </SectionSelect>

      {extraLocationContent}

      <ScopeWrapper>
        <MobileFormController $isSmall onSubmit={onSubmit}>
          <SectionCircle isSmall disabled={disabled}>
            <CircleButton isSmall disabled={disabled}>
              <CircleHole isSmall disabled={disabled}>
                <LogoImg src={logoSrc} />
              </CircleHole>
            </CircleButton>
          </SectionCircle>

          <MobileHole isSmall>
            <MobileTop isSmall disabled={disabled}>
              {hasInput && (
                <MobileInputTemp
                  $isSmall
                  type='text'
                  onChange={onTempChange}
                  value={tempInput}
                  placeholder={placeholder}
                  disabled={disabled}
                />
              )}

              <MobileHeatSection $isSmall $wide={wide} disabled={disabled}>
                <MobileHeatBtn
                  onClick={onApply}
                  $isSmall
                  $wide={wide}
                  disabled={disabled}
                >
                  {labelSmall || label}
                </MobileHeatBtn>
              </MobileHeatSection>
            </MobileTop>
          </MobileHole>
        </MobileFormController>
      </ScopeWrapper>

      {messageBox}
    </MobileWrapper>
  );
};

export default MobileControlLayout;



const MobileFormController = styled.form`
  width: 310px;
  height: 68px;
  border-radius: 34px;
  ${layerA180Deg};
  ${flexBoxCenter};

  ${(p) =>
    p.$isMargin &&
    css`
      margin-bottom: 6px;
    `}

  ${(p) =>
    p.$isSmall &&
    css`
      width: 299px;
      height: 50px;
      border-radius: 31px;
      ${justifyContentSpaceBetween}
      padding: 0 3.5px 0 6px;
    `};
`;

const MobileInputTemp = styled.input`
  width: 91px;
  height: 36px;
  border-radius: 18px;
  ${layerADark};
  font-size: 14px;

  &::placeholder {
    ${(p) =>
      p.disabled
        ? css`
            color: #808080;
          `
        : css`
            color: var(--text-placeholder);
          `}
  }

  ${(p) =>
    p.$isSmall &&
    css`
      width: 91px;
      height: 30px;
    `}

  ${(p) =>
    p.disabled &&
    css`
      ${layerBDisabled}
    `}
`;

const MobileHeatSection = styled.section`
  border-radius: 18px;
  ${layerA}
  ${flexBoxCenter}

  width: ${(p) =>
    p.$isSmall ? (p.$wide ? '231px' : '137px') : p.$wide ? '188px' : '93px'};
  height: ${(p) =>
    p.$isSmall ? (p.$wide ? '28px' : '30px') : '36px'};

  ${(p) =>
    p.disabled &&
    css`
      ${layerBDisabled}
    `}
`;

const MobileHeatBtn = styled.button`
  border-radius: 17px;
  ${layerA180Deg}
  ${flexBoxCenter}
  font-size: 10px;
  line-height: 95%;
  padding-top: 2px;

  width: ${(p) =>
    p.$isSmall
      ? p.$wide
        ? '229px'
        : '135px'
      : p.$wide
      ? '186px'
      : '91px'};
  height: ${(p) => (p.$isSmall ? '28px' : '34px')};

  ${(p) =>
    !p.$isSmall &&
    !p.$wide &&
    css`
      letter-spacing: -0.5px;
    `}

  ${(p) =>
    p.$isSmall &&
    p.$wide &&
    css`
      font-size: 12px;
    `}

  ${(p) =>
    p.disabled &&
    css`
      ${layerADisabled180Deg}
      cursor: not-allowed;
    `}
`;
